import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  Clock,
  User,
  Users,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  X,
  Repeat
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AppointmentEditModal } from './AppointmentEditModal';

// Participant in an appointment (organizer or attendee)
interface Participant {
  id: string;
  type: 'staff' | 'client';
  name: string;
  status?: 'pending' | 'confirmed' | 'declined' | 'tentative';
}

// Full appointment interface (matches API response)
interface Appointment {
  id: string;
  organizer: Participant;
  attendees: Participant[];
  title: string;
  notes?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  created_at: string;
  updated_at: string;
  // Series fields (Phase 7D+)
  series_id?: string | null;
  series_rule?: {
    frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
    count?: number;
    until?: string;
    no_end_date?: boolean;
    days_of_week?: number[]; // 0=Sun, 1=Mon, ... 6=Sat
    // Monthly-specific options
    monthly_type?: 'day_of_month' | 'ordinal_weekday';
    day_of_month?: number; // 1-31
    ordinal?: 'first' | 'second' | 'third' | 'fourth' | 'last';
    weekday?: number; // 0-6 for ordinal_weekday
  } | null;
  instance_index?: number | null;
  is_exception?: boolean;
}

// BusyBlock is now the full appointment
type BusyBlock = Appointment;

interface SchedulingAssistantProps {
  clientId: string;
  clientName: string;
  clientTimezone?: string;
  initialDate?: string;
  initialEditAppointment?: Appointment; // If provided, start in edit mode for this appointment
  initialEditScope?: 'single' | 'series'; // For series edits: 'single' = just this one, 'series' = this and all future
  onAppointmentCreated: () => void;
  onCancel: () => void;
}

/**
 * SCHEDULING ASSISTANT - Phase 7B
 *
 * Client-Anchored Timeline for booking appointments (Outlook Scheduling Assistant style)
 *
 * Key Features:
 * - Timeline displayed in CLIENT's timezone
 * - Coach's busy blocks shown at TZ-adjusted positions
 * - Hover tooltip shows both coach and client times
 * - Click to select time slot
 * - Visual conflict detection
 */
export const SchedulingAssistant: React.FC<SchedulingAssistantProps> = ({
  clientId,
  clientName,
  clientTimezone = 'America/New_York',
  initialDate,
  initialEditAppointment,
  initialEditScope,
  onAppointmentCreated,
  onCancel
}) => {
  const { user } = useAuth();
  const coachTimezone = user?.timezone || 'America/New_York';

  // Selected date (YYYY-MM-DD in client's timezone context)
  const [selectedDate, setSelectedDate] = useState(() => {
    return initialDate || new Date().toISOString().split('T')[0];
  });

  // Coach's busy blocks for the selected date
  const [coachBusyBlocks, setCoachBusyBlocks] = useState<BusyBlock[]>([]);
  const [_loading, setLoading] = useState(false);

  // Selected time range for new appointment
  const [selectedStart, setSelectedStart] = useState<number | null>(null);
  const [selectedEnd, setSelectedEnd] = useState<number | null>(null);

  // Appointment form
  const [title, setTitle] = useState('Coaching Session');
  const [duration, setDuration] = useState(60);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recurring appointment options (Phase 7D+)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [recurringCount, setRecurringCount] = useState(4);
  const [recurringEndType, setRecurringEndType] = useState<'count' | 'until' | 'never'>('count');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState<number[]>([]); // 0=Sun, 1=Mon, ... 6=Sat
  // Monthly-specific options
  const [monthlyType, setMonthlyType] = useState<'day_of_month' | 'ordinal_weekday'>('day_of_month');
  const [monthlyDayOfMonth, setMonthlyDayOfMonth] = useState(15);
  const [monthlyOrdinal, setMonthlyOrdinal] = useState<'first' | 'second' | 'third' | 'fourth' | 'last'>('first');
  const [monthlyWeekday, setMonthlyWeekday] = useState(3); // Wednesday

  // Edit mode - when editing an existing appointment in the timeline (green bar)
  // null = creating new, string = editing existing appointment ID
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  // Edit scope for series appointments: 'single' = just this one, 'series' = this and all future
  const [editScope, setEditScope] = useState<'single' | 'series' | null>(null);
  // Store original appointment info for series reschedule
  const [editingSeriesInfo, setEditingSeriesInfo] = useState<{
    seriesId: string;
    instanceIndex: number;
    originalStart: string;
  } | null>(null);

  // Viewing appointment - for the details modal (step 1 before editing)
  const [viewingAppointment, setViewingAppointment] = useState<BusyBlock | null>(null);

  // Dragging state for repositioning the selection bar
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; startHour: number; duration: number } | null>(null);
  const justFinishedDraggingRef = useRef(false);

  // Resizing state for adjusting start/end times by dragging edges
  const [isResizing, setIsResizing] = useState<'left' | 'right' | null>(null);
  const resizeStartRef = useRef<{ x: number; startHour: number; endHour: number } | null>(null);

  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    block?: BusyBlock;
  }>({ visible: false, x: 0, y: 0 });

  // Timeline configuration
  const START_HOUR = 7; // 7 AM
  const END_HOUR = 21; // 9 PM
  const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
  const PIXELS_PER_HOUR = 80;

  const timelineRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLInputElement>(null);

  // Load coach's busy blocks when date changes
  useEffect(() => {
    loadCoachSchedule();
  }, [selectedDate]);

  // Initialize edit mode if initialEditAppointment is provided
  useEffect(() => {
    if (!initialEditAppointment) return;

    // Set date from appointment
    const appointmentDate = initialEditAppointment.start_time.split('T')[0];
    setSelectedDate(appointmentDate);

    // Wait for next tick to ensure getClientHourFromUTC can process correctly
    setTimeout(() => {
      // Set up edit mode
      const startHour = getClientHourFromUTC(initialEditAppointment.start_time);
      const endHour = getClientHourFromUTC(initialEditAppointment.end_time);

      setSelectedStart(startHour);
      setSelectedEnd(endHour);
      setTitle(initialEditAppointment.title);
      setDuration(initialEditAppointment.duration_minutes);
      setEditingAppointmentId(initialEditAppointment.id);

      // Initialize edit scope for series appointments (passed from ClientCalendarView)
      if (initialEditScope) {
        console.log('[SchedulingAssistant] Initializing from props - scope:', initialEditScope, 'series_id:', initialEditAppointment.series_id);
        setEditScope(initialEditScope);
        if (initialEditScope === 'series' && initialEditAppointment.series_id) {
          setEditingSeriesInfo({
            seriesId: initialEditAppointment.series_id,
            instanceIndex: initialEditAppointment.instance_index ?? 0,
            originalStart: initialEditAppointment.start_time
          });
        }
      }
    }, 0);
  }, [initialEditAppointment, initialEditScope]);

  const loadCoachSchedule = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        `/api/appointments/availability/${user?.id}?date=${selectedDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setCoachBusyBlocks(data.busy_blocks || []);
      }
    } catch (error) {
      console.error('Error loading coach schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate dates - preserve edit state when editing
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
    // Preserve edit state, just clear time selection
    if (editingAppointmentId) {
      setSelectedStart(null);
      setSelectedEnd(null);
      setError(null);
    } else {
      clearSelection();
    }
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
    // Preserve edit state, just clear time selection
    if (editingAppointmentId) {
      setSelectedStart(null);
      setSelectedEnd(null);
      setError(null);
    } else {
      clearSelection();
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    // Preserve edit state, just clear time selection
    if (editingAppointmentId) {
      setSelectedStart(null);
      setSelectedEnd(null);
      setError(null);
    } else {
      clearSelection();
    }
  };

  // Open date picker when clicking on date display
  const openDatePicker = () => {
    datePickerRef.current?.showPicker();
  };

  // Handle date picker change
  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setSelectedDate(e.target.value);
      // When editing, preserve the edit state but clear time selection
      // so user can pick a new time on the new date
      if (editingAppointmentId) {
        // Keep edit mode active - just reset the time selection
        setSelectedStart(null);
        setSelectedEnd(null);
        setError(null);
      } else {
        // Not editing - full clear is fine
        clearSelection();
      }
    }
  };

  // Handle busy block click - open details modal (step 1)
  const handleBusyBlockClick = (e: React.MouseEvent, block: BusyBlock) => {
    e.stopPropagation(); // Prevent timeline click
    setTooltip({ visible: false, x: 0, y: 0 }); // Hide tooltip
    setViewingAppointment(block); // Open details modal
  };

  // Handle "Edit" button from details modal - switch to edit mode (green bar)
  const handleEditInTimeline = (scope: 'single' | 'series' = 'single') => {
    if (!viewingAppointment) return;

    const block = viewingAppointment;

    // Debug: Log what we're working with
    console.log('[SchedulingAssistant] handleEditInTimeline - scope:', scope, 'block.series_id:', block.series_id, 'block:', block);

    // Convert block's times to timeline positions
    const startHour = getClientHourFromUTC(block.start_time);
    const endHour = getClientHourFromUTC(block.end_time);

    // Set selection to match the appointment
    setSelectedStart(startHour);
    setSelectedEnd(endHour);
    setTitle(block.title);
    setDuration(block.duration_minutes);
    setEditingAppointmentId(block.id);
    setEditScope(scope);
    setError(null);

    // Store series info if editing a series
    if (scope === 'series' && block.series_id) {
      setEditingSeriesInfo({
        seriesId: block.series_id,
        instanceIndex: block.instance_index ?? 0,
        originalStart: block.start_time
      });
    } else {
      setEditingSeriesInfo(null);
    }

    // Close the modal
    setViewingAppointment(null);
  };

  // Handle modal close/update/delete - refresh data
  const handleAppointmentUpdated = () => {
    setViewingAppointment(null);
    loadCoachSchedule();
  };

  const handleAppointmentDeleted = () => {
    setViewingAppointment(null);
    loadCoachSchedule();
  };

  // Clear selection and exit edit mode
  const clearSelection = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
    setEditingAppointmentId(null);
    setEditScope(null);
    setEditingSeriesInfo(null);
    setTitle('Coaching Session');
    setError(null);
  };

  // Refs to track state for ESC handler (avoids re-registering handler on every change)
  const selectedStartRef = useRef<number | null>(null);
  selectedStartRef.current = selectedStart;
  const viewingAppointmentRef = useRef<BusyBlock | null>(null);
  viewingAppointmentRef.current = viewingAppointment;

  // ESC handler - close modals/selection in order of priority
  // Registered ONCE on mount, uses refs to check current state
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      // Priority 1: Close appointment details modal if open
      if (viewingAppointmentRef.current !== null) {
        e.stopImmediatePropagation();
        e.preventDefault();
        setViewingAppointment(null);
        return;
      }

      // Priority 2: Cancel active selection (green bar) if present
      if (selectedStartRef.current !== null) {
        e.stopImmediatePropagation();
        e.preventDefault();
        clearSelection();
        return;
      }

      // Otherwise: let event propagate to ClientCalendarView to close scheduling assistant
    };

    window.addEventListener('keydown', handleEscKey, true);
    return () => window.removeEventListener('keydown', handleEscKey, true);
  }, []); // Empty deps - register once on mount

  // Convert UTC time to client timezone hour offset
  const getClientHourFromUTC = (utcTimeStr: string): number => {
    const utcDate = new Date(utcTimeStr);
    // Get the time in client's timezone
    const clientTimeStr = utcDate.toLocaleString('en-US', {
      timeZone: clientTimezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const [hours, minutes] = clientTimeStr.split(':').map(Number);
    return hours + minutes / 60;
  };

  // Convert client timezone hour to UTC for a given date
  // e.g., "11am in EST" -> 16:00 UTC
  const getUTCFromClientHour = (dateStr: string, clientHour: number): Date => {
    const hours = Math.floor(clientHour);
    const minutes = Math.round((clientHour - hours) * 60);

    // Create target time as if it were UTC (just to have a reference point)
    const asUTC = new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000Z`);

    // Get the timezone offset for clientTimezone at this date
    // by comparing how the same instant displays in UTC vs clientTimezone
    const utcString = asUTC.toLocaleString('en-US', { timeZone: 'UTC' });
    const clientString = asUTC.toLocaleString('en-US', { timeZone: clientTimezone });

    // Parse both (browser local timezone doesn't matter - we only use the difference)
    const utcParsed = new Date(utcString);
    const clientParsed = new Date(clientString);

    // offsetMs = how many ms behind UTC the client timezone is
    // For EST (UTC-5): offsetMs = +5 hours (18000000 ms)
    // For PST (UTC-8): offsetMs = +8 hours
    const offsetMs = utcParsed.getTime() - clientParsed.getTime();

    // To convert "11am client time" to UTC: add the offset
    // 11am EST + 5 hours = 4pm UTC (16:00)
    return new Date(asUTC.getTime() + offsetMs);
  };

  // Format time for display
  const formatTimeInTZ = (hour: number, tz: string, includeAmPm = true): string => {
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    if (includeAmPm) {
      return `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm}`;
    }
    return `${displayHour}:${String(minutes).padStart(2, '0')}`;
  };

  // Get block position and width in pixels
  const getBlockStyle = (block: BusyBlock) => {
    const startHour = getClientHourFromUTC(block.start_time);
    const endHour = getClientHourFromUTC(block.end_time);

    const left = (startHour - START_HOUR) * PIXELS_PER_HOUR;
    const width = (endHour - startHour) * PIXELS_PER_HOUR;

    return {
      left: `${left}px`,
      width: `${Math.max(width, 20)}px` // Minimum width for visibility
    };
  };

  // Handle timeline click for selection - simple click to set start time
  const handleTimelineClick = (e: React.MouseEvent) => {
    // Don't create new selection if currently dragging or just finished dragging
    if (isDragging || justFinishedDraggingRef.current) return;
    if (!timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const hour = (x / PIXELS_PER_HOUR) + START_HOUR;

    // Snap to 15-minute intervals
    const snappedHour = Math.round(hour * 4) / 4;

    // Clamp to valid range
    const clampedStart = Math.max(START_HOUR, Math.min(snappedHour, END_HOUR - (duration / 60)));

    setSelectedStart(clampedStart);
    setSelectedEnd(clampedStart + (duration / 60));
  };

  // Handle drag start on selection bar
  const handleSelectionMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent timeline click
    e.preventDefault(); // Prevent browser's native drag behavior (fixes "no-drop" cursor issue)
    if (selectedStart === null || selectedEnd === null) return;

    // Guard: ensure we have valid numbers before starting drag
    if (isNaN(selectedStart) || isNaN(selectedEnd)) return;

    // Store duration at drag start to avoid stale closure issues during drag
    const currentDuration = selectedEnd - selectedStart;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, startHour: selectedStart, duration: currentDuration };
  };

  // Handle dragging - update position while maintaining duration
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current || !timelineRef.current) return;

      const { x: startX, startHour, duration: storedDuration } = dragStartRef.current;

      // Guard: if stored values are invalid, abort
      if (isNaN(startHour) || isNaN(storedDuration)) return;

      const deltaX = e.clientX - startX;
      const deltaHours = deltaX / PIXELS_PER_HOUR;
      const newStart = startHour + deltaHours;

      // Snap to 15-minute intervals
      const snappedStart = Math.round(newStart * 4) / 4;

      // Clamp to valid range
      const clampedStart = Math.max(START_HOUR, Math.min(snappedStart, END_HOUR - storedDuration));
      const clampedEnd = clampedStart + storedDuration;

      // Final guard: only update if values are valid numbers
      if (!isNaN(clampedStart) && !isNaN(clampedEnd)) {
        setSelectedStart(clampedStart);
        setSelectedEnd(clampedEnd);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
      // Prevent click event from firing on timeline after drag
      justFinishedDraggingRef.current = true;
      setTimeout(() => {
        justFinishedDraggingRef.current = false;
      }, 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]); // Removed selectedStart/selectedEnd - we use ref values now

  // Handle resize start on left/right edge
  const handleResizeMouseDown = (e: React.MouseEvent, edge: 'left' | 'right') => {
    e.stopPropagation(); // Prevent drag and timeline click
    e.preventDefault(); // Prevent browser's native drag behavior
    if (selectedStart === null || selectedEnd === null) return;
    if (isNaN(selectedStart) || isNaN(selectedEnd)) return;

    setIsResizing(edge);
    resizeStartRef.current = { x: e.clientX, startHour: selectedStart, endHour: selectedEnd };
  };

  // Handle resizing - adjust start or end time
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current || !timelineRef.current) return;

      const { x: startX, startHour, endHour } = resizeStartRef.current;
      const deltaX = e.clientX - startX;
      const deltaHours = deltaX / PIXELS_PER_HOUR;

      // Minimum duration: 15 minutes (0.25 hours)
      const MIN_DURATION = 0.25;

      if (isResizing === 'left') {
        // Adjusting start time
        const newStart = startHour + deltaHours;
        const snappedStart = Math.round(newStart * 4) / 4; // Snap to 15-min
        // Clamp: can't go before START_HOUR or past (endHour - MIN_DURATION)
        const clampedStart = Math.max(START_HOUR, Math.min(snappedStart, endHour - MIN_DURATION));

        if (!isNaN(clampedStart)) {
          setSelectedStart(clampedStart);
          // Update duration to match new selection
          const newDuration = Math.round((endHour - clampedStart) * 60);
          setDuration(newDuration);
        }
      } else {
        // Adjusting end time
        const newEnd = endHour + deltaHours;
        const snappedEnd = Math.round(newEnd * 4) / 4; // Snap to 15-min
        // Clamp: can't go past END_HOUR or before (startHour + MIN_DURATION)
        const clampedEnd = Math.max(startHour + MIN_DURATION, Math.min(snappedEnd, END_HOUR));

        if (!isNaN(clampedEnd)) {
          setSelectedEnd(clampedEnd);
          // Update duration to match new selection
          const newDuration = Math.round((clampedEnd - startHour) * 60);
          setDuration(newDuration);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(null);
      resizeStartRef.current = null;
      // Prevent click event from firing
      justFinishedDraggingRef.current = true;
      setTimeout(() => {
        justFinishedDraggingRef.current = false;
      }, 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Check if selection conflicts with existing appointments (excluding the one being edited)
  const hasConflict = (): boolean => {
    if (selectedStart === null || selectedEnd === null) return false;

    return coachBusyBlocks.some(block => {
      // Skip the appointment being edited
      if (editingAppointmentId && block.id === editingAppointmentId) return false;

      const blockStart = getClientHourFromUTC(block.start_time);
      const blockEnd = getClientHourFromUTC(block.end_time);
      return selectedStart < blockEnd && selectedEnd > blockStart;
    });
  };

  // Handle block hover for tooltip
  const handleBlockHover = (e: React.MouseEvent, block: BusyBlock) => {
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      block
    });
  };

  const handleBlockLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0 });
  };

  // Get timezone abbreviation
  const getTzAbbreviation = (tz: string): string => {
    const abbrevMap: Record<string, string> = {
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Phoenix': 'MST',
      'Pacific/Honolulu': 'HST'
    };
    return abbrevMap[tz] || tz.split('/').pop()?.replace('_', ' ') || tz;
  };

  // Create or update the appointment
  const handleSaveAppointment = async () => {
    if (selectedStart === null || selectedEnd === null) {
      setError('Please select a time slot on the timeline');
      return;
    }

    if (hasConflict()) {
      setError('Selected time conflicts with an existing appointment');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const token = localStorage.getItem('auth_token');

      // Convert selected client time to UTC
      const startUTC = getUTCFromClientHour(selectedDate, selectedStart);
      const endUTC = getUTCFromClientHour(selectedDate, selectedEnd);

      const isEditing = editingAppointmentId !== null;

      if (isEditing) {
        // Debug: Log edit scope and series info
        console.log('[SchedulingAssistant] Save - editScope:', editScope, 'editingSeriesInfo:', editingSeriesInfo);

        // Check if we're editing a series (scope='series') or single appointment
        if (editScope === 'series') {
          // Verify we have series info - if not, something went wrong
          if (!editingSeriesInfo) {
            console.error('[SchedulingAssistant] ERROR: editScope is "series" but editingSeriesInfo is null!');
            setError('Failed to edit series: missing series information. Try again.');
            return;
          }
          // SERIES EDIT: Use series reschedule endpoint to set all future appointments to the SAME time
          // Send the absolute new time - backend will apply this time-of-day to all future appointments
          console.log('[SchedulingAssistant] SERIES RESCHEDULE - seriesId:', editingSeriesInfo.seriesId,
            'instanceIndex:', editingSeriesInfo.instanceIndex,
            'newStart:', startUTC.toISOString(),
            'newEnd:', endUTC.toISOString());

          const rescheduleResponse = await fetch(`/api/appointments/series/${editingSeriesInfo.seriesId}/reschedule`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              from_instance: editingSeriesInfo.instanceIndex,
              new_start_time: startUTC.toISOString(),
              new_end_time: endUTC.toISOString(),
              reason: 'Rescheduled via timeline (series)'
            })
          });

          if (!rescheduleResponse.ok) {
            const data = await rescheduleResponse.json();
            setError(data.error || 'Failed to reschedule series');
            return;
          }

          // If title was also changed, we'd need a series update endpoint
          // For now, just refresh
          onAppointmentCreated();
        } else if (!editScope || editScope === 'single') {
          // SINGLE EDIT: Use single appointment reschedule endpoint
          const rescheduleResponse = await fetch(`/api/appointments/${editingAppointmentId}/reschedule`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              start_time: startUTC.toISOString(),
              end_time: endUTC.toISOString(),
              reason: 'Rescheduled via timeline'
            })
          });

          if (!rescheduleResponse.ok) {
            const data = await rescheduleResponse.json();
            setError(data.error || 'Failed to reschedule appointment');
            return;
          }

          // If title was also changed, update it via PUT
          const existingBlock = coachBusyBlocks.find(b => b.id === editingAppointmentId);
          if (existingBlock && existingBlock.title !== title) {
            await fetch(`/api/appointments/${editingAppointmentId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ title })
            });
          }

          onAppointmentCreated();
        }
      } else if (isRecurring) {
        // CREATING RECURRING SERIES: Use POST /api/appointments/series
        // Build series_rule based on end type
        const seriesRule: {
          frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
          count?: number;
          until?: string;
          no_end_date?: boolean;
          days_of_week?: number[];
          monthly_type?: 'day_of_month' | 'ordinal_weekday';
          day_of_month?: number;
          ordinal?: 'first' | 'second' | 'third' | 'fourth' | 'last';
          weekday?: number;
        } = {
          frequency: recurringFrequency
        };

        // Add end condition based on type
        if (recurringEndType === 'count') {
          seriesRule.count = recurringCount;
        } else if (recurringEndType === 'until') {
          if (!recurringEndDate) {
            setError('Please select an end date');
            setCreating(false);
            return;
          }
          seriesRule.until = recurringEndDate;
        } else if (recurringEndType === 'never') {
          seriesRule.no_end_date = true;
        }

        // Add days_of_week for weekly frequency (if any selected)
        if (recurringFrequency === 'weekly' && recurringDaysOfWeek.length > 0) {
          seriesRule.days_of_week = recurringDaysOfWeek;
        }

        // Add monthly-specific options
        if (recurringFrequency === 'monthly') {
          seriesRule.monthly_type = monthlyType;
          if (monthlyType === 'day_of_month') {
            seriesRule.day_of_month = monthlyDayOfMonth;
          } else if (monthlyType === 'ordinal_weekday') {
            seriesRule.ordinal = monthlyOrdinal;
            seriesRule.weekday = monthlyWeekday;
          }
        }

        const response = await fetch('/api/appointments/series', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            organizer: {
              id: user?.id,
              type: 'staff',
              name: user?.name || user?.email || 'Coach'
            },
            attendees: [{
              id: clientId,
              type: 'client',
              name: clientName,
              status: 'pending'
            }],
            title,
            start_time: startUTC.toISOString(),
            end_time: endUTC.toISOString(),
            series_rule: seriesRule
          })
        });

        if (response.ok) {
          // Reset recurring options after successful creation
          setIsRecurring(false);
          setRecurringFrequency('weekly');
          setRecurringCount(4);
          setRecurringEndType('count');
          setRecurringEndDate('');
          setRecurringDaysOfWeek([]);
          onAppointmentCreated();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to create recurring appointments');
        }
      } else {
        // CREATING SINGLE APPOINTMENT: Use POST /api/appointments
        const response = await fetch('/api/appointments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            organizer: {
              id: user?.id,
              type: 'staff',
              name: user?.name || user?.email || 'Coach'
            },
            attendees: [{
              id: clientId,
              type: 'client',
              name: clientName,
              status: 'pending'
            }],
            title,
            start_time: startUTC.toISOString(),
            end_time: endUTC.toISOString()
          })
        });

        if (response.ok) {
          onAppointmentCreated();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to create appointment');
        }
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError(`Failed to ${editingAppointmentId ? 'update' : 'create'} appointment`);
    } finally {
      setCreating(false);
    }
  };

  // Delete the appointment being edited
  const handleDeleteAppointment = async () => {
    if (!editingAppointmentId) return;

    try {
      setCreating(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/${editingAppointmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        onAppointmentCreated(); // Refresh and close
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete appointment');
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setError('Failed to delete appointment');
    } finally {
      setCreating(false);
    }
  };

  const formattedDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="scheduling-assistant">
      {/* Header */}
      <div className="sa-header">
        <h2>
          <Users size={20} />
          {editingAppointmentId ? 'Edit Appointment' : 'Schedule Appointment'} with {clientName}
        </h2>
      </div>

      {/* Timeline Legend */}
      <div className="sa-legend">
        <div className="legend-item">
          <span className="legend-color confirmed"></span>
          <span>Confirmed</span>
        </div>
        <div className="legend-item">
          <span className="legend-color pending"></span>
          <span>Pending</span>
        </div>
        <div className="legend-item">
          <span className="legend-color tentative"></span>
          <span>Tentative</span>
        </div>
        <div className="legend-item">
          <span className="legend-color completed"></span>
          <span>Completed</span>
        </div>
        <div className="legend-item">
          <span className="legend-color selected"></span>
          <span>Selected</span>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="sa-timeline-container">
        {/* Hour Headers */}
        <div className="sa-hour-headers">
          <div className="sa-row-label"></div>
          {HOURS.map(hour => (
            <div key={hour} className="sa-hour-header" style={{ width: `${PIXELS_PER_HOUR}px` }}>
              {formatTimeInTZ(hour, clientTimezone)}
            </div>
          ))}
        </div>

        {/* Coach Row */}
        <div className="sa-timeline-row">
          <div className="sa-row-label">
            <User size={14} />
            <span>Coach</span>
            <span className="tz-badge">{getTzAbbreviation(coachTimezone)}</span>
          </div>
          <div
            className="sa-timeline-track"
            ref={timelineRef}
            style={{ width: `${HOURS.length * PIXELS_PER_HOUR}px` }}
            onClick={handleTimelineClick}
          >
            {/* Hour grid lines */}
            {HOURS.map(hour => (
              <div
                key={hour}
                className="sa-hour-line"
                style={{ left: `${(hour - START_HOUR) * PIXELS_PER_HOUR}px` }}
              />
            ))}

            {/* Busy blocks - hide the one being edited */}
            {/* Organizer row: always show as confirmed (green) */}
            {coachBusyBlocks
              .filter(block => block.id !== editingAppointmentId)
              .map(block => (
              <div
                key={block.id}
                className={`sa-busy-block status-confirmed ${block.status === 'completed' ? 'completed' : ''}`}
                style={getBlockStyle(block)}
                onMouseEnter={(e) => handleBlockHover(e, block)}
                onMouseLeave={handleBlockLeave}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => handleBusyBlockClick(e, block)}
                title="Double-click to edit"
              />
            ))}

            {/* Selected time range - draggable with resize handles */}
            {selectedStart !== null && selectedEnd !== null && (
              <div
                className={`sa-selection ${hasConflict() ? 'conflict' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
                style={{
                  left: `${(selectedStart - START_HOUR) * PIXELS_PER_HOUR}px`,
                  width: `${(selectedEnd - selectedStart) * PIXELS_PER_HOUR}px`
                }}
                onMouseDown={handleSelectionMouseDown}
                title="Drag to reposition, edges to resize"
              >
                {/* Left resize handle */}
                <div
                  className="resize-handle left"
                  onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
                  title="Drag to adjust start time"
                />
                {/* Right resize handle */}
                <div
                  className="resize-handle right"
                  onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
                  title="Drag to adjust end time"
                />
              </div>
            )}
          </div>
        </div>

        {/* Client Row (for visual reference) */}
        <div className="sa-timeline-row client-row">
          <div className="sa-row-label">
            <User size={14} />
            <span>{clientName.split(' ')[0]}</span>
            <span className="tz-badge">{getTzAbbreviation(clientTimezone)}</span>
          </div>
          <div className="sa-timeline-track" style={{ width: `${HOURS.length * PIXELS_PER_HOUR}px` }}>
            {HOURS.map(hour => (
              <div
                key={hour}
                className="sa-hour-line"
                style={{ left: `${(hour - START_HOUR) * PIXELS_PER_HOUR}px` }}
              />
            ))}
            {/* Show busy blocks for this client based on their attendee status */}
            {coachBusyBlocks
              .filter(block => block.id !== editingAppointmentId)
              .filter(block => {
                const attendee = block.attendees?.find(a => a.id === clientId);
                // Only show if attendee exists and hasn't declined
                return attendee && attendee.status !== 'declined';
              })
              .map(block => {
                const attendee = block.attendees?.find(a => a.id === clientId);
                const attendeeStatus = attendee?.status || 'pending';
                return (
                  <div
                    key={block.id}
                    className={`sa-busy-block status-${attendeeStatus} ${block.status === 'completed' ? 'completed' : ''}`}
                    style={getBlockStyle(block)}
                    onMouseEnter={(e) => handleBlockHover(e, block)}
                    onMouseLeave={handleBlockLeave}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => handleBusyBlockClick(e, block)}
                    title="Double-click to edit"
                  />
                );
              })}
            {/* Show proposed time on client row too */}
            {selectedStart !== null && selectedEnd !== null && (
              <div
                className={`sa-selection proposed ${hasConflict() ? 'conflict' : ''}`}
                style={{
                  left: `${(selectedStart - START_HOUR) * PIXELS_PER_HOUR}px`,
                  width: `${(selectedEnd - selectedStart) * PIXELS_PER_HOUR}px`
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Appointment Form */}
      <div className="sa-form">
        <div className="form-row">
          {/* Date Navigation - inline with form */}
          <div className="form-date-nav">
            <span className="form-label">Date</span>
            <div className="date-nav-controls">
              <button className="nav-btn" onClick={goToPreviousDay} title="Previous day">
                <ChevronLeft size={14} />
              </button>
              <button
                className="date-picker-trigger"
                onClick={openDatePicker}
                title="Click to pick a date"
              >
                <Calendar size={12} />
                <span>{formattedDate}</span>
              </button>
              <input
                ref={datePickerRef}
                type="date"
                className="hidden-date-picker"
                value={selectedDate}
                onChange={handleDatePickerChange}
              />
              <button className="nav-btn" onClick={goToNextDay} title="Next day">
                <ChevronRight size={14} />
              </button>
              <button className="nav-btn today-btn" onClick={goToToday}>Today</button>
            </div>
          </div>
          <label>
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Coaching Session"
            />
          </label>
          <label>
            <span>Duration</span>
            <select value={duration} onChange={(e) => {
              const newDuration = Number(e.target.value);
              setDuration(newDuration);
              if (selectedStart !== null) {
                setSelectedEnd(selectedStart + (newDuration / 60));
              }
            }}>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </label>
        </div>

        {/* Recurring Options - only show when creating new (not editing) */}
        {!editingAppointmentId && (
          <div className="recurring-options">
            <label className="recurring-toggle">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
              />
              <Repeat size={14} />
              <span>Make this a recurring appointment</span>
            </label>

            {isRecurring && (
              <div className="recurring-config">
                {/* Recurrence Pattern Section */}
                <div className="recurrence-section">
                  <div className="section-title">Recurrence pattern</div>
                  <div className="pattern-row">
                    <span className="pattern-label">Repeat</span>
                    <select
                      value={recurringFrequency}
                      onChange={(e) => {
                        const newFreq = e.target.value as 'daily' | 'weekly' | 'biweekly' | 'monthly';
                        setRecurringFrequency(newFreq);
                        if (newFreq !== 'weekly') {
                          setRecurringDaysOfWeek([]);
                        }
                      }}
                      className="pattern-select"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {/* Days of Week - only show for weekly frequency */}
                  {recurringFrequency === 'weekly' && (
                    <div className="pattern-row days-row">
                      <span className="pattern-label">On</span>
                      <div className="days-checkboxes">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                          <label key={day} className="day-check">
                            <input
                              type="checkbox"
                              checked={recurringDaysOfWeek.includes(index)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRecurringDaysOfWeek([...recurringDaysOfWeek, index].sort((a, b) => a - b));
                                } else {
                                  setRecurringDaysOfWeek(recurringDaysOfWeek.filter(d => d !== index));
                                }
                              }}
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly Options - Outlook-style */}
                  {recurringFrequency === 'monthly' && (
                    <div className="monthly-options">
                      <label className="monthly-option">
                        <input
                          type="radio"
                          name="monthlyType"
                          checked={monthlyType === 'day_of_month'}
                          onChange={() => setMonthlyType('day_of_month')}
                        />
                        <span>Day</span>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={monthlyDayOfMonth}
                          onChange={(e) => setMonthlyDayOfMonth(Number(e.target.value))}
                          disabled={monthlyType !== 'day_of_month'}
                          className="monthly-day-input"
                        />
                        <span>of every month</span>
                      </label>

                      <label className="monthly-option">
                        <input
                          type="radio"
                          name="monthlyType"
                          checked={monthlyType === 'ordinal_weekday'}
                          onChange={() => setMonthlyType('ordinal_weekday')}
                        />
                        <span>The</span>
                        <select
                          value={monthlyOrdinal}
                          onChange={(e) => setMonthlyOrdinal(e.target.value as 'first' | 'second' | 'third' | 'fourth' | 'last')}
                          disabled={monthlyType !== 'ordinal_weekday'}
                          className="monthly-ordinal-select"
                        >
                          <option value="first">first</option>
                          <option value="second">second</option>
                          <option value="third">third</option>
                          <option value="fourth">fourth</option>
                          <option value="last">last</option>
                        </select>
                        <select
                          value={monthlyWeekday}
                          onChange={(e) => setMonthlyWeekday(Number(e.target.value))}
                          disabled={monthlyType !== 'ordinal_weekday'}
                          className="monthly-weekday-select"
                        >
                          <option value={0}>Sunday</option>
                          <option value={1}>Monday</option>
                          <option value={2}>Tuesday</option>
                          <option value={3}>Wednesday</option>
                          <option value={4}>Thursday</option>
                          <option value={5}>Friday</option>
                          <option value={6}>Saturday</option>
                        </select>
                        <span>of every month</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Range of Recurrence Section */}
                <div className="recurrence-section">
                  <div className="section-title">Range of recurrence</div>

                  <label className="range-option">
                    <input
                      type="radio"
                      name="endType"
                      checked={recurringEndType === 'never'}
                      onChange={() => setRecurringEndType('never')}
                    />
                    <span>No end date</span>
                    <span className="range-hint">(max 1 year)</span>
                  </label>

                  <label className="range-option">
                    <input
                      type="radio"
                      name="endType"
                      checked={recurringEndType === 'count'}
                      onChange={() => setRecurringEndType('count')}
                    />
                    <span>End after</span>
                    <input
                      type="number"
                      min="2"
                      max="52"
                      value={recurringCount}
                      onChange={(e) => setRecurringCount(Number(e.target.value))}
                      disabled={recurringEndType !== 'count'}
                      className="range-input-number"
                    />
                    <span>occurrences</span>
                  </label>

                  <label className="range-option">
                    <input
                      type="radio"
                      name="endType"
                      checked={recurringEndType === 'until'}
                      onChange={() => setRecurringEndType('until')}
                    />
                    <span>End by</span>
                    <input
                      type="date"
                      value={recurringEndDate}
                      onChange={(e) => setRecurringEndDate(e.target.value)}
                      disabled={recurringEndType !== 'until'}
                      min={selectedDate}
                      className="range-input-date"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected Time Display */}
        {selectedStart !== null && selectedEnd !== null && (
          <div className="selected-time-display">
            <Clock size={16} />
            <div className="time-info">
              <div className="time-row">
                <span className="label">{clientName}'s time:</span>
                <span className="time">{formatTimeInTZ(selectedStart, clientTimezone)} - {formatTimeInTZ(selectedEnd, clientTimezone)} {getTzAbbreviation(clientTimezone)}</span>
              </div>
              <div className="time-row coach-time">
                <span className="label">Your time:</span>
                <span className="time">
                  {/* Convert to coach timezone for display */}
                  {formatTimeInTZ(selectedStart + getTimezoneOffsetDiff(clientTimezone, coachTimezone), coachTimezone)} - {formatTimeInTZ(selectedEnd + getTimezoneOffsetDiff(clientTimezone, coachTimezone), coachTimezone)} {getTzAbbreviation(coachTimezone)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="form-actions">
          {editingAppointmentId && (
            <button
              className="btn-delete"
              onClick={handleDeleteAppointment}
              disabled={creating}
            >
              <X size={16} />
              Delete
            </button>
          )}
          <button className="btn-cancel" onClick={editingAppointmentId ? clearSelection : onCancel}>
            {editingAppointmentId ? 'Cancel Edit' : 'Cancel'}
          </button>
          <button
            className="btn-create"
            onClick={handleSaveAppointment}
            disabled={creating || selectedStart === null || hasConflict()}
          >
            {creating ? (
              <>{editingAppointmentId ? 'Saving...' : (isRecurring ? 'Creating Series...' : 'Creating...')}</>
            ) : (
              <>
                {isRecurring ? <Repeat size={16} /> : <Check size={16} />}
                {editingAppointmentId
                  ? (editScope === 'series' ? 'Update Series' : 'Update Appointment')
                  : (isRecurring
                      ? (recurringEndType === 'count'
                          ? `Create ${recurringCount} Appointments`
                          : recurringEndType === 'until'
                            ? 'Create Series (until date)'
                            : 'Create Series (ongoing)')
                      : 'Create Appointment')
                }
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.visible && tooltip.block && (
        <div
          className="sa-tooltip"
          style={{
            left: `${tooltip.x + 10}px`,
            top: `${tooltip.y + 10}px`
          }}
        >
          <div className="tooltip-title">{tooltip.block.title}</div>
          <div className="tooltip-times">
            <div className="tooltip-time">
              <span className="label">Coach:</span>
              <span>{new Date(tooltip.block.start_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: coachTimezone
              })} - {new Date(tooltip.block.end_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: coachTimezone
              })} {getTzAbbreviation(coachTimezone)}</span>
            </div>
            <div className="tooltip-time">
              <span className="label">{clientName.split(' ')[0]}:</span>
              <span>{new Date(tooltip.block.start_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: clientTimezone
              })} - {new Date(tooltip.block.end_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: clientTimezone
              })} {getTzAbbreviation(clientTimezone)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Details Modal */}
      {viewingAppointment && (
        <AppointmentEditModal
          appointment={viewingAppointment}
          clientName={
            // Find the client attendee's name, or use provided clientName if this is the current client
            viewingAppointment.attendees.find(a => a.type === 'client')?.name ||
            (viewingAppointment.attendees.some(a => a.id === clientId) ? clientName : 'Client')
          }
          clientTimezone={clientTimezone}
          isOpen={true}
          onClose={() => setViewingAppointment(null)}
          onUpdated={handleAppointmentUpdated}
          onDeleted={handleAppointmentDeleted}
          onEditInTimeline={handleEditInTimeline}
        />
      )}

      <style>{styles}</style>
    </div>
  );
};

// Helper to get timezone offset difference in hours
function getTimezoneOffsetDiff(tz1: string, tz2: string): number {
  const now = new Date();
  const date1 = new Date(now.toLocaleString('en-US', { timeZone: tz1 }));
  const date2 = new Date(now.toLocaleString('en-US', { timeZone: tz2 }));
  return (date2.getTime() - date1.getTime()) / (60 * 60 * 1000);
}

const styles = `
  .scheduling-assistant {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 90vh;
    overflow-y: auto;
  }

  /* Header */
  .sa-header h2 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .sa-header h2 svg {
    color: var(--accent-green);
  }

  /* Legend */
  .sa-legend {
    display: flex;
    gap: 20px;
    padding: 8px 12px;
    background: var(--bg-primary);
    border-radius: var(--radius-small);
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .legend-color {
    width: 16px;
    height: 12px;
    border-radius: 2px;
  }

  .legend-color.confirmed {
    background: rgba(16, 185, 129, 0.3);
    border: 1px solid #10b981;
  }

  .legend-color.pending {
    background: rgba(245, 158, 11, 0.3);
    border: 1px solid #f59e0b;
  }

  .legend-color.tentative {
    background: rgba(59, 130, 246, 0.3);
    border: 1px solid #3b82f6;
  }

  .legend-color.completed {
    background: rgba(107, 114, 128, 0.3);
    border: 1px solid #6b7280;
  }

  .legend-color.selected {
    background: rgba(16, 185, 129, 0.3);
    border: 2px dashed var(--accent-green);
  }

  /* Timeline Container */
  .sa-timeline-container {
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    overflow-x: auto;
    background: var(--bg-primary);
  }

  /* Hour Headers - Sticky */
  .sa-hour-headers {
    display: flex;
    border-bottom: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
    min-width: fit-content;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .sa-hour-header {
    flex-shrink: 0;
    padding: 6px 4px;
    font-size: 10px;
    color: var(--text-secondary);
    text-align: center;
    border-right: 1px solid var(--border-secondary);
  }

  /* Row Label - Sticky freeze panes */
  .sa-row-label {
    min-width: 140px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary);
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-secondary);
    position: sticky;
    left: 0;
    z-index: 5;
  }

  .sa-hour-headers .sa-row-label {
    min-width: 140px;
    padding: 6px 10px;
  }

  .tz-badge {
    font-size: 9px;
    font-weight: 400;
    color: var(--text-tertiary);
    background: var(--bg-primary);
    padding: 1px 5px;
    border-radius: 6px;
    margin-left: auto;
  }

  /* Timeline Row - Compact */
  .sa-timeline-row {
    display: flex;
    height: 36px;
    border-bottom: 1px solid var(--border-secondary);
    min-width: fit-content;
  }

  .sa-timeline-row:last-child {
    border-bottom: none;
  }

  .sa-timeline-row.client-row {
    background: var(--bg-secondary);
  }

  /* Timeline Track */
  .sa-timeline-track {
    position: relative;
    height: 100%;
    cursor: crosshair;
  }

  .sa-timeline-track.empty {
    cursor: default;
  }

  .sa-hour-line {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--border-secondary);
  }

  /* Busy Block - Base styles */
  .sa-busy-block {
    position: absolute;
    top: 3px;
    bottom: 3px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    padding: 0 6px;
    overflow: hidden;
    cursor: pointer;
    z-index: 2;
  }

  /* Confirmed - Green (organizer or accepted) */
  .sa-busy-block.status-confirmed {
    background: rgba(16, 185, 129, 0.2);
    border: 1px solid rgba(16, 185, 129, 0.5);
  }

  .sa-busy-block.status-confirmed:hover {
    background: rgba(16, 185, 129, 0.3);
    border-color: #10b981;
  }

  /* Pending - Yellow/Amber (awaiting response) */
  .sa-busy-block.status-pending {
    background: rgba(245, 158, 11, 0.2);
    border: 1px solid rgba(245, 158, 11, 0.5);
  }

  .sa-busy-block.status-pending:hover {
    background: rgba(245, 158, 11, 0.3);
    border-color: #f59e0b;
  }

  /* Tentative - Blue (soft commitment) */
  .sa-busy-block.status-tentative {
    background: rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.5);
  }

  .sa-busy-block.status-tentative:hover {
    background: rgba(59, 130, 246, 0.3);
    border-color: #3b82f6;
  }

  /* Completed appointments - gray/muted (overrides status colors) */
  .sa-busy-block.completed {
    background: rgba(107, 114, 128, 0.2) !important;
    border-color: rgba(107, 114, 128, 0.5) !important;
  }

  .sa-busy-block.completed:hover {
    background: rgba(107, 114, 128, 0.3) !important;
    border-color: #6b7280 !important;
  }

  /* Selection */
  .sa-selection {
    position: absolute;
    top: 3px;
    bottom: 3px;
    background: rgba(16, 185, 129, 0.2);
    border: 2px solid var(--accent-green);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    cursor: grab;
    user-select: none; /* Prevent text selection during drag */
    -webkit-user-select: none;
  }

  .sa-selection.conflict {
    background: rgba(239, 68, 68, 0.3);
    border-color: #ef4444;
  }

  .sa-selection.dragging {
    cursor: grabbing;
    opacity: 0.8;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .sa-selection.proposed {
    background: rgba(16, 185, 129, 0.1);
    border-style: dashed;
    border-width: 1px;
  }

  .selection-time {
    font-size: 11px;
    font-weight: 500;
    color: var(--accent-green);
    white-space: nowrap;
  }

  .sa-selection.conflict .selection-time {
    color: #dc2626;
  }

  .selection-label {
    font-size: 10px;
    color: var(--accent-green);
    opacity: 0.8;
  }

  /* Resize Handles */
  .resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 8px;
    cursor: ew-resize;
    z-index: 4;
    background: transparent;
    transition: background 0.15s;
  }

  .resize-handle:hover,
  .sa-selection.resizing .resize-handle {
    background: rgba(16, 185, 129, 0.3);
  }

  .resize-handle.left {
    left: -4px;
    border-radius: 4px 0 0 4px;
  }

  .resize-handle.right {
    right: -4px;
    border-radius: 0 4px 4px 0;
  }

  .sa-selection.resizing {
    cursor: ew-resize;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .sa-selection.conflict .resize-handle:hover,
  .sa-selection.conflict.resizing .resize-handle {
    background: rgba(239, 68, 68, 0.3);
  }

  /* Form */
  .sa-form {
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    padding: 16px;
  }

  .form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-row label {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-row label span {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .form-row input,
  .form-row select {
    padding: 10px 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 14px;
    color: var(--text-primary);
  }

  .form-row input:focus,
  .form-row select:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  /* Date Navigation in Form Row */
  .form-date-nav {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-date-nav .form-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .date-nav-controls {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    padding: 4px 8px;
  }

  .date-nav-controls .nav-btn {
    padding: 4px 6px;
    background: transparent;
    border: none;
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .date-nav-controls .nav-btn:hover {
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .date-nav-controls .today-btn {
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    margin-left: 4px;
  }

  .date-picker-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: transparent;
    border: none;
    border-radius: var(--radius-small);
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .date-picker-trigger:hover {
    background: var(--bg-primary);
    color: var(--accent-green);
  }

  .date-picker-trigger svg {
    color: var(--text-tertiary);
  }

  .date-picker-trigger:hover svg {
    color: var(--accent-green);
  }

  .hidden-date-picker {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    border: none;
    padding: 0;
    pointer-events: none;
  }

  /* Selected Time Display */
  .selected-time-display {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    background: rgba(16, 185, 129, 0.05);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: var(--radius-medium);
    margin-bottom: 16px;
  }

  .selected-time-display svg {
    color: var(--accent-green);
    margin-top: 2px;
  }

  .time-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .time-row {
    display: flex;
    gap: 8px;
    font-size: 13px;
  }

  .time-row .label {
    color: var(--text-secondary);
    min-width: 100px;
  }

  .time-row .time {
    color: var(--text-primary);
    font-weight: 500;
  }

  .time-row.coach-time {
    opacity: 0.8;
  }

  /* Error Message */
  .error-message {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: var(--radius-medium);
    color: #dc2626;
    font-size: 13px;
    margin-bottom: 16px;
  }

  /* Form Actions */
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .btn-cancel {
    padding: 10px 20px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-cancel:hover {
    background: var(--bg-secondary);
    border-color: var(--text-secondary);
  }

  .btn-create {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--accent-green);
    border: none;
    border-radius: var(--radius-medium);
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-create:hover:not(:disabled) {
    background: #059669;
  }

  .btn-create:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-delete {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: var(--radius-medium);
    color: #dc2626;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s;
    margin-right: auto;
  }

  .btn-delete:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.2);
    border-color: #dc2626;
  }

  .btn-delete:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Tooltip */
  .sa-tooltip {
    position: fixed;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    max-width: 250px;
  }

  .tooltip-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .tooltip-times {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tooltip-time {
    display: flex;
    gap: 8px;
    font-size: 12px;
  }

  .tooltip-time .label {
    color: var(--text-secondary);
    min-width: 50px;
  }

  .tooltip-time span:last-child {
    color: var(--text-primary);
  }

  /* Recurring Options */
  .recurring-options {
    margin-bottom: 16px;
    padding: 12px;
    background: var(--bg-secondary);
    border-radius: var(--radius-medium);
    border: 1px solid var(--border);
  }

  .recurring-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .recurring-toggle input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--accent-green);
    cursor: pointer;
  }

  .recurring-toggle svg {
    color: var(--accent-green);
  }

  .recurring-config {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }

  /* Side-by-side on larger screens */
  .recurring-config > .recurrence-section {
    flex: 1;
    min-width: 0;
  }

  /* Stack on smaller screens */
  @media (max-width: 700px) {
    .recurring-config {
      flex-direction: column;
    }
  }

  /* Recurrence Sections (Outlook-style) */
  .recurrence-section {
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    padding: 12px;
    display: flex;
    flex-direction: column;
  }

  .section-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }

  /* Pattern Row (Repeat + Days) */
  .pattern-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  .pattern-row:last-child {
    margin-bottom: 0;
  }

  .pattern-label {
    font-size: 13px;
    color: var(--text-secondary);
    min-width: 50px;
  }

  .pattern-select {
    padding: 6px 10px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .pattern-select:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  /* Days Checkboxes (Outlook-style inline) */
  .days-row {
    flex-wrap: wrap;
  }

  .days-checkboxes {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .day-check {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .day-check input[type="checkbox"] {
    accent-color: var(--accent-green);
    cursor: pointer;
  }

  .day-check span {
    user-select: none;
  }

  /* Monthly Options (Outlook-style) */
  .monthly-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  }

  .monthly-option {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .monthly-option input[type="radio"] {
    accent-color: var(--accent-green);
    cursor: pointer;
    margin: 0;
  }

  .monthly-day-input {
    width: 50px;
    padding: 4px 8px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 13px;
    color: var(--text-primary);
    text-align: center;
  }

  .monthly-day-input:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .monthly-ordinal-select,
  .monthly-weekday-select {
    padding: 4px 8px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .monthly-ordinal-select:disabled,
  .monthly-weekday-select:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Range Options (Outlook-style stacked radio) */
  .range-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
  }

  .range-option input[type="radio"] {
    accent-color: var(--accent-green);
    cursor: pointer;
    margin: 0;
  }

  .range-option > span:first-of-type {
    min-width: 70px;
  }

  .range-input-number {
    width: 60px;
    padding: 4px 8px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 13px;
    color: var(--text-primary);
    text-align: center;
  }

  .range-input-number:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .range-input-date {
    padding: 4px 8px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    font-size: 13px;
    color: var(--text-primary);
  }

  .range-input-date:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .range-hint {
    font-size: 11px;
    color: var(--text-tertiary);
    font-style: italic;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .scheduling-assistant {
      padding: 16px;
    }

    .form-row {
      flex-direction: column;
    }

    .sa-row-label {
      width: 80px;
      font-size: 11px;
    }

    .sa-hour-headers .sa-row-label {
      width: 80px;
    }
  }
`;

export default SchedulingAssistant;
